import type { NotificationDetail } from '@risksmart-app/components/src/notifications/types';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import i18next from '@risksmart-app/i18n/src/i18n';
import { isTRPCClientError } from '@trpc/client';

import { ChangeRequestConfirmationCancelledError } from '@/hooks/mutations/_with-change-request-confirm';
import { tryExtractChangeRequestEditNotAllowed } from '@/utils/formError';
import { hasErrorCode, hasErrorMessage } from '@/utils/graphqlUtils';

/**
 * Sentinel an `asyncAction` can resolve to so `mutationResultNotification`
 * produces NO toast — neither success nor failure. Used for user-cancelled
 * no-op flows, e.g. dismissing the change-request confirm modal. (RSP-5788)
 */
export const MUTATION_NO_OP = Symbol('mutation-no-op');

export type MutationActionResult = boolean | typeof MUTATION_NO_OP;

type Options<T> = {
  asyncAction: (data: T) => Promise<MutationActionResult>;
  failureAction?: () => void;
  entityName: string;
  /**
   * Set to true to avoid a notification popup
   */
  disableNotification?: boolean;
};

type NoParaOptions = {
  asyncAction: () => Promise<MutationActionResult>;
  failureAction?: () => void;
  entityName: string;
};

type OptionsWithMessageKey<T> = Options<T> & {
  successMessageKey:
    | 'create_success_message'
    | 'delete_success_message'
    | 'remove_success_message'
    | 'update_success_message';
};

/**
 * Generic fallback for a mutation rejection.
 */
// TODO: translation
const GENERIC_FAILURE_MESSAGE = 'Failed to save changes';

const LEGACY_OPTIMISTIC_LOCK_MESSAGE =
  'Item has been modified since last viewed. Please refresh page and try again';

const STANDARD_TRPC_OPTIMISTIC_LOCK_MESSAGE =
  'Record has been modified by another user. Please refresh and try again.';

const TRPC_OPTIMISTIC_LOCK_MESSAGES: Readonly<Record<string, string>> = {
  'frontend.entity.update': STANDARD_TRPC_OPTIMISTIC_LOCK_MESSAGE,
  'frontend.ingestionConfig.update':
    'This ingestion config was modified by another user. Please refresh and try again.',
  'frontend.internalAuditReport.update': STANDARD_TRPC_OPTIMISTIC_LOCK_MESSAGE,
  'frontend.obligation.update': STANDARD_TRPC_OPTIMISTIC_LOCK_MESSAGE,
  'frontend.riskAssessmentResultConfig.update':
    'Risk assessment result configuration has been modified by another user',
  'frontend.taxonomy.update':
    'Records not updated. Record may have been updated by another user',
  'frontend.thirdParty.update': STANDARD_TRPC_OPTIMISTIC_LOCK_MESSAGE,
};

/**
 * Returns the server's actionable optimistic-lock message, but only for the
 * allowlisted concurrency signals the frontend contract recognises. Arbitrary
 * Apollo/tRPC messages remain behind the caller's generic fallback.
 */
export const tryResolveOptimisticLockConflict = (
  error: unknown
): string | null => {
  if (!(error instanceof Error)) {
    return null;
  }

  if (isTRPCClientError(error) && error.data?.code === 'CONFLICT') {
    const expectedMessage = error.data.path
      ? TRPC_OPTIMISTIC_LOCK_MESSAGES[error.data.path]
      : undefined;

    return expectedMessage && error.message === expectedMessage
      ? expectedMessage
      : null;
  }

  // The exact legacy message or extension code is the allowlisted
  // discriminator; any accompanying message is still remote input. Return fixed
  // product copy so a malformed legacy response cannot surface arbitrary
  // internal text in the UI.
  //
  // Both accessors scan every GraphQL error, matching the `.find()` this
  // replaced — Hasura returns the conflict alongside unrelated errors, so a
  // first-error read would miss it. They also answer for a tRPC error, which is
  // unreachable here in practice (the CONFLICT branch above returns first, and
  // neither discriminator is a tRPC code or tRPC-side copy) and harmless if it
  // ever were: the copy returned would still describe the error.
  if (
    hasErrorMessage(error, LEGACY_OPTIMISTIC_LOCK_MESSAGE) ||
    hasErrorCode(error, 'modified-since-last-view')
  ) {
    return LEGACY_OPTIMISTIC_LOCK_MESSAGE;
  }

  return null;
};

/**
 * Picks the toast copy for a rejected mutation.
 *
 * The bulk paths (bulk delete, in particular) have no form to render a field
 * error into, so every rejection collapsed into `GENERIC_FAILURE_MESSAGE` —
 * including the terminal in-flight-edit block, whose whole point is that it
 * carries a clear, actionable sentence. `mapFormError` already surfaces that on
 * the single-entity form path; this is the same projection for the paths that
 * only have a toast (COI-900).
 *
 * Deliberately narrow: it reads the allowlist-stripped
 * `error.shape.data.cause` via `tryExtractChangeRequestEditNotAllowed`, so only
 * a cause the server explicitly whitelisted for the wire can change the copy.
 * Anything else — an opaque 500, a network failure — keeps the generic message
 * rather than leaking a raw server string into the UI.
 */
const resolveErrorMessage = (error: unknown): string => {
  const optimisticLockMessage = tryResolveOptimisticLockConflict(error);
  if (optimisticLockMessage) {
    return optimisticLockMessage;
  }

  if (!tryExtractChangeRequestEditNotAllowed(error)) {
    return GENERIC_FAILURE_MESSAGE;
  }

  return error instanceof Error && error.message
    ? error.message
    : GENERIC_FAILURE_MESSAGE;
};

export const mutationResultNotification = <T>({
  asyncAction,
  failureAction,
  successMessageKey,
  entityName,
  addNotification,
  disableNotification,
}: OptionsWithMessageKey<T> & {
  addNotification: (notification: NotificationDetail) => void;
}) => {
  return async (data: T) => {
    const promise = asyncAction(data).catch((error: unknown) => {
      if (error instanceof ChangeRequestConfirmationCancelledError) {
        return MUTATION_NO_OP;
      }
      throw error;
    });
    if (disableNotification) {
      return promise;
    }

    addNotification({
      type: 'promise',
      promise: promise.then((result) => {
        // A no-op result must produce neither a success nor a failure toast —
        // rethrow the sentinel so `suppressOn` below dismisses it silently.
        if (result === MUTATION_NO_OP) {
          throw MUTATION_NO_OP;
        }
        if (!result) {
          failureAction?.();
          throw new Error('Unexpected error');
        }
      }),
      successMessage: i18next.t(successMessageKey, {
        entity: entityName || 'Item',
      }),
      errorMessage: resolveErrorMessage,
      suppressOn: (error) => error === MUTATION_NO_OP,
    });

    return promise;
  };
};

const useMutationResultNotification = <T>(
  options: OptionsWithMessageKey<T>
) => {
  const { addNotification } = useNotifications();

  return mutationResultNotification({
    ...options,
    addNotification,
  });
};

export const useRemoveResultNotification = ({
  asyncAction,
  failureAction,
  entityName: entityLabel,
}: NoParaOptions) => {
  const hook = useMutationResultNotification({
    asyncAction,
    failureAction,
    entityName: entityLabel,
    successMessageKey: 'remove_success_message',
  });

  return () => hook({});
};

export const useDeleteResultNotification = ({
  asyncAction,
  failureAction,
  entityName: entityLabel,
}: NoParaOptions) => {
  const hook = useMutationResultNotification({
    asyncAction,
    failureAction,
    entityName: entityLabel,
    successMessageKey: 'delete_success_message',
  });

  return () => hook({});
};

export const useUpdateResultNotification = <T>({
  asyncAction,
  failureAction,
  entityName: entityLabel,
}: Options<T>) => {
  return useMutationResultNotification({
    asyncAction,
    failureAction,
    entityName: entityLabel,
    successMessageKey: 'update_success_message',
  });
};

export const useCreateResultNotification = <T>({
  asyncAction,
  failureAction,
  entityName: entityLabel,
}: Options<T>) => {
  return useMutationResultNotification({
    asyncAction,
    failureAction,
    entityName: entityLabel,
    successMessageKey: 'create_success_message',
  });
};
